<template>
    <UsageLimitModal :visible="true" @close="onClose" :list="[]" :button-text="buttonText" :upgrade-url="upgradeUrl" @upgrade="handleUpgrade" :custom-upgrade-jump="customUpgrade">
        <template #description>
            <span>{{ description }}</span>
        </template>
    </UsageLimitModal>
</template>

<script setup lang="ts">
import { UsageLimitModal } from 'patsnap-biz';
import { useTrack } from '../../composables/use-track';
import { computed, onMounted } from 'vue';
import '@patsnap-biz/usage-limit-modal/lib/style';
import _ from 'lodash';
import { useI18n } from 'vue-i18n';
import { getCoreEurekaPricingUrl } from '@eureka/utils/core-redirect';
import { openClueReport } from '../../clue-report';
import { EurekaPackageType } from '@eureka/services/src/apis/common.api.types';
import { EurekaVersion } from '@eureka/shared/edition-upgrade/src/constants';
import { isCNRegion } from '@eureka/utils/environment';
import { usePlg } from '@eureka/shared/edition-upgrade';

interface Props {
    callback: () => void;
    eventDetail?: Record<string, any>;
    /**
     * 功能受限
     */
    functionLimit?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
});

const { t } = useI18n({
    messages: {
        cn: {
            upgradeDialogModal: {
                contactUs: '联系我们',
                functionLimit_desc: '升级套餐后，即可使用此功能，并获得更高使用额度。',
                functionLimit_header: '解锁更多能力',
                personal_free: '你的免费额度不足，请升级至 Pro 继续分析。',
                personal_pro: '你的可用额度不足，请升级套餐或联系销售获取更多额度。',
                enterprise: '你的可用额度不足，请联系管理员获取更多额度。',
                cn_free: '你的免费额度不足，请联系销售获取更多额度。',
            },
        },
        en: {
            upgradeDialogModal: {
                contactUs: 'Contact us',
                functionLimit_desc: 'Upgrade your plan to access this feature and get higher usage limits.',
                functionLimit_header: 'Unlock more capabilities',
                personal_free: 'Your free quota is insufficient. Upgrade to Pro to continue your analysis.',
                personal_pro: 'Your available usage quota is insufficient. Please upgrade your plan or contact sales to get more access.',
                enterprise: 'Your available usage quota is insufficient. Please contact your administrator for more access.',
                cn_free: 'Your free quota is insufficient. Please contact sales to get more access.',
            },
        },
        jp: {
            upgradeDialogModal: {
                contactUs: 'お問い合わせ',
                functionLimit_desc: 'プランをアップグレードすると、この機能を利用でき、より高い利用枠もご利用いただけます。',
                functionLimit_header: 'さらに多くの機能を利用する',
                personal_free: '無料利用枠が不足しています。分析を続けるには Pro にアップグレードしてください。',
                personal_pro: 'ご利用可能な枠が不足しています。プランをアップグレードするか、営業担当者にお問い合わせのうえ、利用枠を追加してください。',
                enterprise: 'ご利用可能な枠が不足しています。利用枠の追加については、管理者にお問い合わせください。',
                cn_free: '無料利用枠が不足しています。利用枠の追加については、営業担当者にお問い合わせください。',
            },
        },
        tw: {
            upgradeDialogModal: {
                contactUs: '聯繫我們',
                functionLimit_desc: '升級方案後，即可使用此功能，並取得更高使用額度。',
                functionLimit_header: '解鎖更多能力',
                personal_free: '你的免費額度不足，請升級至 Pro 以繼續分析。',
                personal_pro: '你的可用額度不足，請升級方案或聯絡銷售取得更多額度。',
                enterprise: '你的可用額度不足，請聯絡管理員取得更多額度。',
                cn_free: '你的免費額度不足，請聯絡銷售取得更多額度。',
            },
        },
    },
});

const track = useTrack();
const { isEnterpriseTenant, isPersonalTenant, isFreeUser, isPro, isEnterpriseCommercialUser, isEnterPriseFreeUser } = usePlg();

const isCn = computed(() => {
    return isCNRegion();
})

const description = computed(() => {
    if (props.functionLimit) {
        return t('upgradeDialogModal.functionLimit_desc')
    }
    if (isCNRegion()) {
        if (isEnterpriseCommercialUser.value) {
            return t('upgradeDialogModal.enterprise')
        } else {
            return t('upgradeDialogModal.cn_free')
        }
    } else {
        if (isFreeUser.value) {
            return t('upgradeDialogModal.personal_free')
        } else if (isPro.value || isEnterPriseFreeUser.value) {
            return t('upgradeDialogModal.personal_pro')
        } else if (isEnterpriseCommercialUser.value) {
            return t('upgradeDialogModal.enterprise')
        }
    }
    return ''
})

const customUpgrade = computed(() => {
    return !(!isCn.value && isPersonalTenant.value)
})

const upgradeUrl = computed(() => {
    if (!customUpgrade.value) {
        const triggerSource = _.get(props.eventDetail, 'trigger_source', 'paywall');
        const agentName = _.get(props.eventDetail, 'agents')?.join(',') || _.get(props.eventDetail, 'agent_name');
        return getCoreEurekaPricingUrl(window.location.href, { triggerSource, agentName, headerType: 'feature_upgrade_required' });
    }
    return undefined
});

const buttonText = computed(() => {
    if (isCn.value) {
        return t('upgradeDialogModal.contactUs')
    } else {
        if (isEnterpriseTenant.value) {
            return t('upgradeDialogModal.contactUs')
        }
    }
    return undefined
})

onMounted(() => {
    const agentName = _.get(props.eventDetail, 'agents');

    track({
        eventType: 'SS EUREKA UPGRADE PAGE',
        sourceType: 'upgrade_trigger',
        moduleType: 'LS',
        eventDetail: {
            product_line: 'eureka_ls',
            agent_name: agentName?.join(','),
            ...(props.eventDetail || {}),
        },
    })
})

function onClose() {
    props.callback();
}

function handleUpgrade() {
    if (upgradeUrl.value) return
    openClueReport({
        pkgType: EurekaPackageType.LS_COMMERCIAL,
        version: EurekaVersion.LS,
    })
}

</script>

<style>
.patsnap-biz-usage-limit-modal .patsnap-biz-usage-limit-modal__list-tick {
    @apply shrink-0;
}

.patsnap-biz-usage-limit-modal .patsnap-biz-usage-limit-modal__button {
    width: 304px;
    max-width: 100%;
}

</style>
